import "./ImageGallery.scss"

import React, { useEffect, useMemo } from "react"
import Image from "gatsby-image/withIEPolyfill"
import classnames from "classnames"
import { useTransition, a } from "react-spring"
import { ResizeObserver } from "@juggle/resize-observer"
import { useMeasure } from "react-use"
import { useMedia } from "use-media"

import {
  and,
  compose,
  curry,
  identity,
  find,
  findIndex,
  head,
  map,
  not,
  isNil,
  prop,
  propEq,
  split,
} from "ramda"

const ImageGallery = React.memo(
  ({
    className,
    list,
    metadata = null,
    showHoverInfo,
    showLabelInfo,
    queries,
    values,
    defaultValue,
  }) => {
    useEffect(() => {
      if (document) {
        const contextMenuListener = document.addEventListener(
          "contextmenu",
          e => e.target.nodeName === "IMG" && e.preventDefault(),
          false
        )
        return () =>
          document.removeEventListener("contextmenu", contextMenuListener)
      }
      return undefined
    })

    const hasActiveHover = useMemo(
      () => and(compose(not, isNil)(metadata), showHoverInfo),
      [metadata, showHoverInfo]
    )
    const hasActiveLabel = useMemo(
      () => and(compose(not, isNil)(metadata), showLabelInfo),
      [metadata, showLabelInfo]
    )

    const labelModifier = 70

    const defaultQueries = [
      "(max-width: 576px)",
      "(max-width: 768px)",
      "(max-width: 992px)",
      "(min-width: 993px)",
    ]
    const parsedQueries = queries ? queries : defaultQueries

    const defaultValues = [1, 1, 2, 2]
    const parsedValues = values ? values : defaultValues
    const parsedDefaultValue = defaultValue ? defaultValue : 3

    // Hook1: Tie media queries to the number of columns
    const columnMatchIndex = compose(
      index => parsedValues[index],
      findIndex(identity),
      map(useMedia)
    )(parsedQueries)
    const columns = parsedValues[columnMatchIndex] || parsedDefaultValue

    // Hook2: Measure the width of the container element
    const [ref, { width }] = useMeasure({
      debounce: 0,
      scroll: false,
      polyfill: ResizeObserver,
    })

    let heights = new Array(columns).fill(0) // Each column gets a height starting with zero
    let gridItems = list.map((child, i) => {
      const column = heights.indexOf(Math.min(...heights)) // Basic masonry-grid placing, puts tile into the smallest column using Math.min

      const xy = [
        (width / columns) * column,
        (heights[column] += hasActiveLabel
          ? child.presentationHeight + labelModifier
          : child.presentationHeight) - child.presentationHeight,
      ]
      return {
        ...child,
        xy,
        width: width / columns,
        height: hasActiveLabel
          ? child.presentationHeight / 2
          : child.presentationHeight / 2,
      }
    })

    // Hook4: Turn the static grid values into animated transitions, any addition, removal or change will be animated
    const transitions = useTransition(gridItems, item => item.src, {
      from: ({ xy, width, presentationHeight }) => ({
        xy,
        width,
        height: presentationHeight,
        opacity: 0,
      }),
      enter: ({ xy, width, presentationHeight }) => ({
        xy,
        width,
        height: presentationHeight,
        opacity: 1,
      }),
      update: ({ xy, width, presentationHeight }) => ({
        xy,
        width,
        height: presentationHeight,
      }),
      leave: { height: 0, opacity: 0 },
      config: { mass: 5, tension: 800, friction: 100 },
      trail: 25,
    })

    const getImageData = curry((metadata, item) =>
      find(
        propEq(
          "originalName",
          compose(head, split("."), prop("originalName"))(item)
        )
      )(metadata)
    )

    const detailsHover = ({ label, excerpt }) =>
      label && excerpt ? (
        <div className="gatsby-image-wrapper-hover">
          <h6 className="info-label">{label}</h6>
          <span className="info-excerpt">{excerpt}</span>
        </div>
      ) : null

    const detailsLabel = ({ label, excerpt }) =>
      label && excerpt ? (
        <div className="gatsby-image-wrapper-label">
          <h6 className="info-label">{label}</h6>
          <span className="info-excerpt">{excerpt}</span>
        </div>
      ) : null

    return (
      <div
        ref={ref}
        className={classnames(
          "image-gallery gallery-list w-100 pb-4 pb-md-0",
          className
        )}
        style={{
          height: Math.max(...heights),
        }}
      >
        {transitions.map(({ item, props: { xy, ...rest }, key }) => (
          <a.div
            key={key}
            style={{
              transform: xy.interpolate(
                (x, y) =>
                  `translate3d(${x}px,${
                    hasActiveLabel ? y - labelModifier : y
                  }px,0)`
              ),
              ...rest,
            }}
          >
            <Image fluid={item} alt="Galleriebild" />
            {hasActiveHover
              ? compose(detailsHover, getImageData)(metadata, item)
              : null}
            {hasActiveLabel
              ? compose(detailsLabel, getImageData)(metadata, item)
              : null}
          </a.div>
        ))}
      </div>
    )
  }
)

export default ImageGallery
